import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs } from "@firebase/firestore";
import { app } from '../../firebase.js';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';

function DemographicsBySource() {
  const db = getFirestore(app);
  const [genderData, setGenderData] = useState([]);
  const [ageData, setAgeData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [conversionByGender, setConversionByGender] = useState([]);
  const [loading, setLoading] = useState(true);

  const COLORS = ['#4a90e2', '#e94e77', '#7b7b7b', '#ffa500']; // Male, Female, Prefer not to say, Unknown

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);

      let male = 0, female = 0, preferNotSay = 0, unknown = 0;
      let ageUnder20 = 0, age20to24 = 0, age25to30 = 0, age30to39 = 0, age40plus = 0;

      const conversionGroups = {
        '<20': { converted: 0, not_converted: 0 },
        '20-24': { converted: 0, not_converted: 0 },
        '25-30': { converted: 0, not_converted: 0 },
        '30-39': { converted: 0, not_converted: 0 },
        '40+': { converted: 0, not_converted: 0 },
      };

      const genderConversions = {
        Male: { converted: 0, not_converted: 0 },
        Female: { converted: 0, not_converted: 0 },
        'Prefer not to say': { converted: 0, not_converted: 0 },
        Unknown: { 0: 0, 0: 0 },
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Gender
        let genderKey = 'Unknown';
        if (data.gender === 'Male') {
          male += 1;
          genderKey = 'Male';
        } else if (data.gender === 'Female') {
          female += 1;
          genderKey = 'Female';
        } else if (data.gender === 'Prefer not to say') {
          preferNotSay += 1;
          genderKey = 'Prefer not to say';
        }

        // Age
        const age = data.age;
        let group = null;
        if (age) {
          if (age < 20) {
            ageUnder20 += 1;
            group = '<20';
          } else if (age >= 20 && age <= 24) {
            age20to24 += 1;
            group = '20-24';
          } else if (age >= 25 && age <= 30) {
            age25to30 += 1;
            group = '25-30';
          } else if (age >= 31 && age <= 39) {
            age30to39 += 1;
            group = '30-39';
          } else if (age >= 40) {
            age40plus += 1;
            group = '40+';
          }
        }

        // Conversion by userStatus
        const status = data.userStatus || 'inactive';
        const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped'];
        const notConvertedStatuses = ['inactive', 'Did Not Join'];

        const isConverted = convertedStatuses.includes(status);
        const isNotConverted = notConvertedStatuses.includes(status);

        if (group) {
          if (isConverted) {
            conversionGroups[group].converted += 1;
          } else if (isNotConverted) {
            conversionGroups[group].not_converted += 1;
          }
        }

        // Conversion by gender
        if (genderKey) {
          if (isConverted) {
            genderConversions[genderKey].converted += 1;
          } else if (isNotConverted) {
            genderConversions[genderKey].not_converted += 1;
          }
        }
      });

      // Gender Data
      const genderArr = [
        { name: 'Male', value: male },
        { name: 'Female', value: female },
        { name: 'Prefer not to say', value: preferNotSay },
      ];

      setGenderData(genderArr);

      // Age Data
      setAgeData([
        { ageGroup: '<20', count: ageUnder20 },
        { ageGroup: '20-24', count: age20to24 },
        { ageGroup: '25-30', count: age25to30 },
        { ageGroup: '30-39', count: age30to39 },
        { ageGroup: '40+', count: age40plus },
      ]);

      // Conversion by Age
      setConversionData(Object.keys(conversionGroups).map((group) => ({
        ageGroup: group,
        Converted: conversionGroups[group].converted,
        'Not Converted': conversionGroups[group].not_converted,
      })));

      // Conversion by Gender
      setConversionByGender(Object.keys(genderConversions).map((g) => ({
        gender: g,
        Converted: genderConversions[g].converted,
        'Not Converted': genderConversions[g].not_converted,
      })));

      setLoading(false);
    };

    fetchData();
  }, [db]);

  return (
    <div className='mainContainer'>
      <LeftBar />
      <div className='theMainContainer'>
        <Dashnav />
        <div className="container mt-4">
          <h4>Demographics</h4>

          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              {/* Gender Pie Chart */}
              <h5 className="mt-4 mb-2">Gender Distribution (June 2024 – Date)</h5>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={genderData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label
                    >
                      {genderData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Age Distribution */}
              <h5 className="mt-5 mb-2">Age Distribution (June 2024 – Date)</h5>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={ageData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ageGroup" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#4a90e2" name="Users" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Conversion by Age */}
              <h5 className="mt-5 mb-2">Conversion by Age Group (June 2024 – Date)</h5>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={conversionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ageGroup" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Converted" fill="#28a745" />
                    <Bar dataKey="Not Converted" fill="#dc3545" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Conversion by Gender */}
              <h5 className="mt-5 mb-2">Conversion by Gender (June 2024 – Date)</h5>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={conversionByGender} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="gender" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Converted" fill="#28a745" />
                    <Bar dataKey="Not Converted" fill="#dc3545" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DemographicsBySource;